import { defineStore } from 'pinia'
import { FetchError } from 'ofetch'
import {
  isSortTheSame,
  isCategoryTheSame,
  isQueryTheSame,
  isEventsIdsTheSame,
  FilterParams,
  setSort,
} from './params-helper'
import type { CheckboxFilterItem } from '~/models/common'
import { NavigationSortType } from '~/models/navigation'
import { VideoSource, VideoEndpointMap } from '~/models/video'
import type { Video, VideoEndpoint } from '~/models/video'

interface VideoState {
  video: Video | null
  isVideoLoaded: boolean
  // eventsFilterItems are mutated by checkboxes in VideoEventsFilter
  eventsFilterItems: CheckboxFilterItem[]
  relatedVideoList: Video[]
  isRelatedVideoListLoaded: boolean
}

export const useVideo = defineStore('video', () => {
  const host = useHost()

  // TODO: https://app.asana.com/0/1202924432057564/1203829013202629
  const baseURL = useRuntimeConfig().public.gatewayApi

  const state = reactive<VideoState>({
    video: null,
    isVideoLoaded: false,
    eventsFilterItems: [],
    relatedVideoList: [],
    isRelatedVideoListLoaded: false,
  })

  const initVideoList = () => {
    const path = VideoEndpointMap.Base
    // TODO: uncomment later
    // We have temporarily disabled the language filter, until there are no translations. This allows user to see more videos

    // const list = useInfinityList<Video>(path, baseURL, (params) => {
    //   params[FilterParams.Language] = i18n.locale.value
    // })
    const list = useInfinityList<Video>(path, baseURL)

    list.state.params = {
      per_page: 6,
    }

    return list
  }

  const videoList = initVideoList()

  async function fetchVideo(id: number, endpoint: VideoEndpoint) {
    if (state.video?.id === Number(id)) {
      return
    }

    if (isNaN(Number(id))) {
      throw useCreateScreenError({ message: 'video id should be a number' })
    }

    state.video = null

    state.isVideoLoaded = false

    try {
      const { data } = await useAuthFetch<{ data: Video }>(
        `${endpoint}/${id}`,
        {
          baseURL,
        }
      )

      state.video = data
    } catch (error: any) {
      if ((error as FetchError).status === 404) {
        throw useCreateScreenError({ message: 'no such video' })
      } else {
        throw error
      }
    } finally {
      state.isVideoLoaded = true
    }
  }

  const resetVideo = () => {
    state.video = null
  }

  function getVideoPageLink(id: number, categorySlug: string) {
    return `${host}/video/${categorySlug}/item-${id}`
  }

  function setEventsFilterItems(items: CheckboxFilterItem[]) {
    state.eventsFilterItems = items
  }

  const eventsFilterActiveIds = computed(() =>
    state.eventsFilterItems.reduce((acc, event) => {
      event.value && acc.push(event.id)
      return acc
    }, [] as number[])
  )

  function onEventsFilterUpdate() {
    if (eventsFilterActiveIds.value.length) {
      videoList.state.params[FilterParams.EventArray] =
        eventsFilterActiveIds.value
    } else {
      delete videoList.state.params[FilterParams.EventArray]
    }

    videoList.reset()
    videoList.getPage()
  }

  function resetVideoListWithFilters(
    endpoint: VideoEndpoint,
    sort: NavigationSortType,
    query?: string,
    categoryId?: number
  ) {
    const isEndpointTheSame = endpoint === videoList.state.endpoint

    if (
      isEndpointTheSame &&
      isSortTheSame(videoList.state.params, sort) &&
      isCategoryTheSame(videoList.state.params, categoryId) &&
      isQueryTheSame(videoList.state.params, query) &&
      isEventsIdsTheSame(videoList.state.params, eventsFilterActiveIds.value)
    ) {
      return
    }

    videoList.state.endpoint = endpoint

    setSort(videoList.state.params, sort)

    if (categoryId) {
      videoList.state.params[FilterParams.Categories] = [categoryId]
    } else {
      delete videoList.state.params[FilterParams.Categories]
    }

    if (query) {
      videoList.state.params[FilterParams.Query] = query
    } else {
      delete videoList.state.params[FilterParams.Query]
    }

    if (eventsFilterActiveIds.value.length) {
      videoList.state.params[FilterParams.EventArray] =
        eventsFilterActiveIds.value
    } else {
      delete videoList.state.params[FilterParams.EventArray]
    }

    videoList.reset()
  }

  const isListHasFilters = computed(() => {
    return (
      videoList.state.params[FilterParams.Query] ||
      videoList.state.params[FilterParams.EventArray]
    )
  })

  function clearEventsFilter() {
    state.eventsFilterItems.forEach((item) => {
      item.value = false
    })
  }

  const fetchRelatedVideoList = async (
    endpoint: VideoEndpoint,
    categoryId: number,
    exceptVideoId: number
  ) => {
    state.relatedVideoList = []
    state.isRelatedVideoListLoaded = false

    try {
      const { data } = await useAuthFetch<{ data: Video[] }>(endpoint, {
        baseURL,
        params: {
          // [FilterParams.Language]: i18n.locale.value,
          [FilterParams.Categories]: [categoryId],
          per_page: 6,
        },
      })

      state.relatedVideoList = data.filter((item) => item.id !== exceptVideoId)
    } catch (error: any) {
      useLogError(error)
    } finally {
      state.isRelatedVideoListLoaded = true
    }
  }

  const VideoPreviewUrlGetters: Record<string, (videoId: string) => string> = {
    [VideoSource.Youtube]: (videoId) =>
      `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
    [VideoSource.GCore]: () => '',
  }

  const VideoPreviewFallbackGetters: Record<
    string,
    (event: Event, id: string) => void
  > = {
    [VideoSource.Youtube]: (event, id) => {
      const imgElement = event.currentTarget as HTMLImageElement

      // if we got youtube placeholder, try low res thumbnail
      if (imgElement.naturalWidth > 0 && imgElement.naturalWidth <= 120) {
        imgElement.src = `https://img.youtube.com/vi/${id}/hqdefault.jpg`
      }
    },
  }

  const VideoUrlGetters: Record<
    string,
    (videoId: string, parent: string) => string
  > = {
    [VideoSource.Youtube]: (videoId, parent) =>
      `https://www.youtube.com/embed/${videoId}?origin=${parent}`,
    [VideoSource.GCore]: (videoId) => {
      const [clientId] = videoId.split('_')

      return `https://${clientId}.gvideo.io/videos/${videoId}`
    },
  }

  // TODO: uncomment later
  // We have temporarily disabled the language filter, until there are no translations. This allows user to see more videos

  // watch(
  //   () => i18n.locale.value,
  //   () => {
  //     videoList.getPage(1)
  //   }
  // )

  watch(
    () => eventsFilterActiveIds.value,
    (oldValue, newValue) => {
      if (
        oldValue.length === newValue.length &&
        oldValue.every((id) => newValue.includes(id))
      ) {
        return
      }

      onEventsFilterUpdate()
    }
  )

  return {
    video: toRef(state, 'video'),
    isVideoLoaded: toRef(state, 'isVideoLoaded'),
    eventsFilterItems: toRef(state, 'eventsFilterItems'),
    relatedVideoList: toRef(state, 'relatedVideoList'),
    isRelatedVideoListLoaded: toRef(state, 'isRelatedVideoListLoaded'),
    list: videoList.state,
    fetchVideo,
    resetVideo,
    allRecordsLoaded: videoList.allRecordsLoaded,
    fetch: videoList.fetch,
    getVideoList: videoList.getPage,
    getPage: videoList.getPage,
    nextPage: videoList.nextPage,
    resetVideoListWithFilters,
    getVideoPageLink,
    isListHasFilters,
    setEventsFilterItems,
    clearEventsFilter,
    eventsFilterActiveIds,
    fetchRelatedVideoList,
    VideoPreviewUrlGetters,
    VideoPreviewFallbackGetters,
    VideoUrlGetters,
  }
})
